'use client'

import { useState } from "react"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import { formatDateNormal } from "@/utils/util"
import ReportVendorDialog from "@/components/blocks/ReportVendorDialog"
import { panelFocusRing } from "../elements/SidePanel"

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `inline-flex items-center justify-center h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 ${panelFocusRing}`
const textButton = `text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[100%_1px] hover:bg-size-[0%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${panelFocusRing}`

const CHECKS = [
  { t: 'Төлбөрөө хамгаалалттай аргаар хий', s: 'Боломжтой бол QPay, StorePay зэрэг буцаалт хийх боломжтой аргыг сонго.' },
  { t: 'Бусдын үнэлгээг уншаарай', s: 'Бүтээгдэхүүний хуудсан дахь бодит сэтгэгдэл, үнэлгээг нягтал.' },
  { t: 'Холбоо барих мэдээллийг шалга', s: 'Утас, имэйл, хаяг бодитоор ажиллаж байгаа эсэхийг нягтал.' },
  { t: 'Сэжигтэй бол мэдэгд', s: 'Хуурамч дэлгүүр гэж бодож байвал бидэнд шууд мэдэгдээрэй.' },
]

export default function Status({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [reportOpen, setReportOpen] = useState(false)
  const verified = !!vendor?.isDanVerified
  const openedDate = vendor?.createdAt ? formatDateNormal(new Date(vendor.createdAt)) : null

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${panelFocusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
        <span>Баталгаажуулалт</span>
      </nav>

      <header className="mt-8 lg:mt-16 max-w-3xl">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-foreground">
          <span aria-hidden className={`size-1.5 rounded-full ${verified ? 'bg-foreground' : 'bg-red-600'}`} />
          {verified ? 'Баталгаажсан' : 'Баталгаажаагүй'}
        </div>
        <h1 className="mt-4 text-4xl sm:text-6xl tracking-tight text-foreground">{vendor?.name}</h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          {verified
            ? 'Энэ дэлгүүрийн эзэмшигчийн хувийн мэдээлэл Улсын бүртгэлээр шалгагдан, жинхэнэ иргэн / аж ахуйн нэгж болох нь баталгаажсан.'
            : 'Энэ дэлгүүрийн эзэмшигчийн хувийн мэдээлэл Улсын бүртгэлээр баталгаажаагүй байна. Худалдан авалт хийхээсээ өмнө доорх зүйлсийг нягтлаарай.'}
        </p>
      </header>

      {verified ? (
        <section className="mt-12 lg:mt-16 max-w-3xl">
          <h2 className={eyebrow}>Бүртгэлийн мэдээлэл</h2>
          <dl className="mt-4 divide-y divide-border border-y border-border text-sm">
            <div className="flex items-baseline justify-between gap-6 py-4"><dt className="text-muted-foreground">Дэлгүүрийн нэр</dt><dd className="text-right">{vendor?.name || '—'}</dd></div>
            {openedDate && <div className="flex items-baseline justify-between gap-6 py-4"><dt className="text-muted-foreground">Нээгдсэн огноо</dt><dd className="text-right tabular-nums">{openedDate}</dd></div>}
            <div className="flex items-baseline justify-between gap-6 py-4"><dt className="text-muted-foreground">Шалгасан байгууллага</dt><dd className="text-right">Улсын бүртгэл (ДАН)</dd></div>
            <div className="flex items-baseline justify-between gap-6 py-4"><dt className="text-muted-foreground">Төлөв</dt><dd className="text-right">Идэвхтэй</dd></div>
          </dl>
          <p className="mt-4 text-xs text-muted-foreground">Эзэмшигчийн хувийн мэдээлэл нийтэд харагдахгүй ба бид аюулгүй хамгаална.</p>
        </section>
      ) : (
        <section className="mt-12 lg:mt-16">
          <h2 className="text-2xl sm:text-4xl tracking-tight">Худалдан авахаасаа өмнө</h2>
          <ol className="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8">
            {CHECKS.map((c, i) => (
              <li key={c.t} className="pt-6 border-t border-foreground">
                <div className="text-xs tabular-nums text-muted-foreground">{String(i + 1).padStart(2, '0')}</div>
                <div className="mt-3 text-base text-foreground">{c.t}</div>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{c.s}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
        <a href={navigateUrl('/', pathname)} className={solidButton}>Дэлгүүр үзэх</a>
        <button type="button" onClick={() => setReportOpen(true)} className={textButton}>Дэлгүүрийг мэдэгдэх</button>
      </div>

      <p className="mt-16 pt-6 border-t border-border max-w-3xl text-xs leading-relaxed text-muted-foreground">
        Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой бөгөөд худалдан авалтын аливаа баталгаа болохгүй.
      </p>

      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </div>
  )
}
